import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Grid, TextField, Typography, Box, Alert, 
  CircularProgress, List, ListItem, ListItemText, 
  ListItemSecondaryAction, IconButton, Divider,
  Accordion, AccordionSummary, AccordionDetails,
  FormControl, InputLabel, Select, MenuItem, Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../utils/api';

interface DocumentVersion {
  id: number;
  version: string;
  changelog: any;
  changelog_summary: string;
  created_by: any;
  created_at: string;
  notes: string;
}

interface DocumentProp {
  id: number;
  title: string;
  version: string;
  file: string;
  is_current_version: boolean;
  version_history?: DocumentVersion[];
}

interface DocumentVersionDialogProps {
  open: boolean;
  onClose: () => void;
  document: DocumentProp | null;
  onVersionCreated: () => void;
}

interface VersionFormData {
  file: File | null;
  description: string;
  changelog_summary: string;
  changelog: {
    added_sections: string[];
    modified_sections: string[];
    removed_sections: string[];
    policy_changes: string[];
    administrative_updates: string[];
  };
  notes: string;
  effective_date: string;
  expiration_date: string;
}

const DocumentVersionDialog: React.FC<DocumentVersionDialogProps> = ({
  open,
  onClose,
  document,
  onVersionCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const [formData, setFormData] = useState<VersionFormData>({
    file: null,
    description: '',
    changelog_summary: '',
    changelog: {
      added_sections: [],
      modified_sections: [],
      removed_sections: [],
      policy_changes: [],
      administrative_updates: []
    },
    notes: '',
    effective_date: '',
    expiration_date: ''
  });

  const [newChangelogItem, setNewChangelogItem] = useState('');
  const [selectedChangelogCategory, setSelectedChangelogCategory] = useState('added_sections');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type and size
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setError('Only PDF files are allowed');
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        setError('File size cannot exceed 25MB');
        return;
      }
      setFormData(prev => ({ ...prev, file }));
    }
  };

  const addChangelogItem = () => {
    if (newChangelogItem.trim()) {
      setFormData(prev => ({
        ...prev,
        changelog: {
          ...prev.changelog,
          [selectedChangelogCategory]: [
            ...prev.changelog[selectedChangelogCategory as keyof typeof prev.changelog],
            newChangelogItem.trim()
          ]
        }
      }));
      setNewChangelogItem('');
    }
  };

  const removeChangelogItem = (category: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      changelog: {
        ...prev.changelog,
        [category]: prev.changelog[category as keyof typeof prev.changelog].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSubmit = async () => {
    if (!document || !formData.file) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const formDataToSend = new FormData();
      formDataToSend.append('file', formData.file);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('changelog_summary', formData.changelog_summary);
      formDataToSend.append('changelog_categories', JSON.stringify(formData.changelog));
      formDataToSend.append('notes', formData.notes);
      
      if (formData.effective_date) {
        formDataToSend.append('effective_date', formData.effective_date);
      }
      if (formData.expiration_date) {
        formDataToSend.append('expiration_date', formData.expiration_date);
      }

      // Don't set Content-Type header - let browser set it automatically for FormData
      await api.post(`/documents/${document.id}/create_version/`, formDataToSend);

      onVersionCreated();
      onClose();
      
      // Reset form
      setFormData({
        file: null,
        description: '',
        changelog_summary: '',
        changelog: {
          added_sections: [],
          modified_sections: [],
          removed_sections: [],
          policy_changes: [],
          administrative_updates: []
        },
        notes: '',
        effective_date: '',
        expiration_date: ''
      });

    } catch (error: any) {
      const data = error.response?.data;
      const message =
        data?.error ||
        data?.detail ||
        (typeof data === 'object' ? Object.values(data).flat().join(', ') : null) ||
        'Failed to create new version';
      setError(message);
      console.error('Error creating version:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'added_sections': return 'Added Sections';
      case 'modified_sections': return 'Modified Sections';
      case 'removed_sections': return 'Removed Sections';
      case 'policy_changes': return 'Policy Changes';
      case 'administrative_updates': return 'Administrative Updates';
      default: return category;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'added_sections': return 'success';
      case 'modified_sections': return 'warning';
      case 'removed_sections': return 'error';
      case 'policy_changes': return 'primary';
      case 'administrative_updates': return 'info';
      default: return 'default';
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Document Versions: {document.title}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Create New Version */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Create New Version
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current Version: <strong>{document.version}</strong>
              </Typography>
              {!document.is_current_version && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  New versions can only be created from the current version of a document. 
                  This is an older version and cannot be revised.
                </Alert>
              )}
            </Box>

            {/* File Upload */}
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: document?.is_current_version ? 'pointer' : 'not-allowed',
                mb: 3,
                opacity: document?.is_current_version ? 1 : 0.5,
                '&:hover': document?.is_current_version ? { borderColor: 'primary.main' } : {}
              }}
              onClick={() => {
                if (!document?.is_current_version) return;
                const fileInput = (window.document as any).getElementById('version-file-input');
                if (fileInput) fileInput.click();
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {formData.file ? formData.file.name : 'Click to upload new PDF version'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PDF files only, maximum 25MB
              </Typography>
              <input
                id="version-file-input"
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                disabled={!document?.is_current_version}
                style={{ display: 'none' }}
              />
            </Box>

            {/* Version Description */}
            <TextField
              fullWidth
              label="Version Description"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              disabled={!document?.is_current_version}
              sx={{ mb: 3 }}
            />

            {/* Changelog Summary */}
            <TextField
              fullWidth
              label="Changelog Summary"
              value={formData.changelog_summary}
              onChange={(e) => setFormData(prev => ({ ...prev, changelog_summary: e.target.value }))}
              placeholder="Brief summary of changes in this version"
              disabled={!document?.is_current_version}
              sx={{ mb: 3 }}
            />

            {/* Structured Changelog */}
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Detailed Changelog</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Category</InputLabel>
                        <Select
                          value={selectedChangelogCategory}
                          label="Category"
                          onChange={(e) => setSelectedChangelogCategory(e.target.value)}
                        >
                          <MenuItem value="added_sections">Added Sections</MenuItem>
                          <MenuItem value="modified_sections">Modified Sections</MenuItem>
                          <MenuItem value="removed_sections">Removed Sections</MenuItem>
                          <MenuItem value="policy_changes">Policy Changes</MenuItem>
                          <MenuItem value="administrative_updates">Administrative Updates</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Change Description"
                        value={newChangelogItem}
                        onChange={(e) => setNewChangelogItem(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addChangelogItem()}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={addChangelogItem}
                        disabled={!newChangelogItem.trim()}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

                {/* Display Changelog Items */}
                {Object.entries(formData.changelog).map(([category, items]) => (
                  items.length > 0 && (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        <Chip 
                          label={getCategoryLabel(category)} 
                          size="small" 
                          color={getCategoryColor(category) as any}
                          sx={{ mr: 1 }}
                        />
                      </Typography>
                      <List dense>
                        {items.map((item, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={item} />
                            <ListItemSecondaryAction>
                              <Button
                                size="small"
                                color="error"
                                onClick={() => removeChangelogItem(category, index)}
                              >
                                Remove
                              </Button>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )
                ))}
              </AccordionDetails>
            </Accordion>

            {/* Dates */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Effective Date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiration Date"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            {/* Notes */}
            <TextField
              fullWidth
              label="Version Notes"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Internal notes about this version"
              sx={{ mt: 2 }}
            />
          </Grid>

          {/* Version History */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Version History
            </Typography>
            
            <List>
              {(document.version_history || [])
                .sort((a, b) => {
                  // Sort by version number (Rev 1, Rev 2, etc.) in descending order
                  const getVersionNumber = (version: string) => {
                    const match = version.match(/Rev (\d+)/);
                    return match ? parseInt(match[1], 10) : 0;
                  };
                  return getVersionNumber(b.version) - getVersionNumber(a.version);
                })
                .map((version, index) => (
                <React.Fragment key={version.id}>
                  <ListItem alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1">
                            {version.version}
                          </Typography>
                          {version.version === document?.version && (
                            <Chip label="Current" size="small" color="success" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(version.created_at)} by {version.created_by ? `${version.created_by.first_name} ${version.created_by.last_name}` : 'Unknown'}
                          </Typography>
                          {version.changelog_summary && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {version.changelog_summary}
                            </Typography>
                          )}
                          {version.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Notes: {version.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton size="small">
                        <ViewIcon />
                      </IconButton>
                      <IconButton size="small">
                        <DownloadIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < (document.version_history || []).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.file || !document?.is_current_version}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Creating Version...' : 'Create New Version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentVersionDialog;